import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAppContext } from '@/context/AppContext';

interface GuardedRouteProps {
  children: ReactNode;
  /** Optional permission required to view this route. */
  permission?: string;
  /**
   * Marks a route as belonging to the customer portal.
   *
   * The two shells are mutually exclusive, and the check runs both ways: an external customer who
   * types a dashboard address is sent to `/portal`, and somebody who works here and lands on a
   * portal address is sent home. Without the second half, an employee following a link from a
   * customer's email would reach a screen that shows them nothing and explains nothing.
   */
  portal?: boolean;
}

/** Redirects to /login when unauthenticated, or home when the identity does not belong here. */
export const GuardedRoute = ({ children, permission, portal }: GuardedRouteProps) => {
  const { isAuthenticated, loading, hasPermission, isPortalUser, homePath } = useAppContext();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!!portal !== isPortalUser) {
    return <Navigate to={homePath} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to={homePath} replace />;
  }

  return <>{children}</>;
};

export default GuardedRoute;
